import { useEffect, useState } from 'react'
import cx from 'classnames'
import { useRouter } from 'next/router'
import { datadogRum } from '@datadog/browser-rum'
import {
  Primary,
  ScreenReaderText,
  SvgIcon,
  Text,
} from '@cinch-labs/design-system'
import { useCompareBar } from './provider/compare-bar-provider'
import styles from './compare-bar.module.css'
import { CompareBarVehicle } from './provider/compare-bar-provider.types'
import { useAnalytics } from '@cinch-nx/shared-analytics'
import {
  compareBarDrawerInteractionEvent,
  compareCtaClickEvent,
  compareVehicleRemovalEvent,
} from './tracking/tracking-events'

interface CompareBarImagePropTypes {
  src: string | undefined
  isSold: boolean
  onClick: () => void
}

const CompareBarImage = ({
  src,
  isSold,
  onClick,
}: CompareBarImagePropTypes) => {
  const [hasError, updateErrorState] = useState(false)

  const updateError = () => {
    updateErrorState(true)
  }

  return !src || hasError ? (
    <div className={styles.compareBarHoldingImage} aria-hidden="true">
      <SvgIcon
        name="vehicle-side"
        size={22}
        color="mercury"
        className={styles.compareBarImageIcon}
      />
    </div>
  ) : (
    <img
      className={cx(styles.compareBarCarImage, {
        [styles.compareBarCarImageSold]: isSold,
      })}
      alt=""
      role="presentation"
      src={src || ''}
      onError={updateError}
      loading="lazy"
      width={60}
      height={34}
      onClick={onClick}
    />
  )
}

export const CompareBar = () => {
  const {
    comparisonVehicles,
    compareBarExpanded,
    compareBarSticky,
    maxNumberOfItems,
    vehicleSummaryMap,
    removeVehicle,
    setCompareBarExpanded,
    customerHasActiveOrder,
  } = useCompareBar()
  const { trackEvent } = useAnalytics()
  const router = useRouter()
  const compareButtonDisabled = comparisonVehicles.length <= 1
  const vehicleIds = comparisonVehicles.map((vehicle) => vehicle.vehicleId)
  const compareCtaUrl = `/compare-cars/${vehicleIds.join(';')}`

  const handleVehicleRemoval = (
    vehicleId: string,
    vehicleMake: string,
    vehicleModel: string,
  ) => {
    removeVehicle(vehicleId)

    trackEvent(
      compareVehicleRemovalEvent(vehicleMake, vehicleModel, comparisonVehicles),
    )

    // TODO: Transfer ownership to PD
    datadogRum.addAction('Compare - Compare Bar vehicle removed', {
      service: 'compare',
      squad: 'search',
      vehicle: {
        vehicleId,
        make: vehicleMake,
        model: vehicleModel,
      },
    })
  }

  const renderVehicleCard = (vehicle: CompareBarVehicle) => {
    const { vehicleId, vehicleTitle, vehicleImage, vehicleMake, vehicleModel } =
      vehicle
    const isSold = vehicleSummaryMap?.[vehicleId]?.isAvailable === false

    const handleOnImageClick = () => {
      router.push(
        `/used-cars/${vehicleMake}/${vehicleModel}/details/${vehicleId}`,
      )
    }

    return (
      <li className={styles.compareBarListItem} key={vehicleId}>
        <article className={styles.compareBarListArticle}>
          <CompareBarImage
            src={vehicleImage}
            isSold={isSold}
            onClick={handleOnImageClick}
          />

          <Text
            element="h3"
            aria-describedby={isSold ? `vehicle-sold-${vehicleId}` : undefined}
            className={cx(styles.compareBarListItemHeading, {
              [styles.compareBarListItemHeadingSold]: isSold,
            })}
          >
            {vehicleTitle}
          </Text>
          <button
            className={styles.compareBarListItemButton}
            type="button"
            onClick={() =>
              handleVehicleRemoval(vehicleId, vehicleMake, vehicleModel)
            }
            aria-label={`Remove ${vehicleTitle} from the compare list`}
          >
            <SvgIcon name="cross" size={16} color="deepPurple" />
          </button>

          {isSold && (
            <Text
              element="span"
              fontSizeFixed="s"
              fontWeight="bold"
              className={styles.compareBarListItemSoldBadge}
              aria-hidden="true"
            >
              Sold
            </Text>
          )}

          {isSold && (
            <ScreenReaderText
              aria-hidden="true"
              id={`vehicle-sold-${vehicleId}`}
            >
              This vehicle has been sold
            </ScreenReaderText>
          )}
        </article>
      </li>
    )
  }

  const renderCompareBarItems = (_: unknown, index: number) => {
    const currentVehicle = comparisonVehicles[index]

    if (currentVehicle) {
      return renderVehicleCard(currentVehicle)
    }

    return (
      <li
        key={index}
        className={cx(
          styles.compareBarListItem,
          styles.compareBarListItemEmpty,
        )}
      >
        You can add another car to compare
      </li>
    )
  }

  const handleCompareBarToggle = (open: boolean) => {
    setCompareBarExpanded(open)
    trackEvent(compareBarDrawerInteractionEvent(open))
  }

  const handleCompareCtaClick = () => {
    trackEvent(compareCtaClickEvent(comparisonVehicles))

    // TODO: Transfer ownership to PD
    datadogRum.addAction('Compare - Bar CTA clicked', {
      service: 'compare',
      squad: 'search',
      compareCount: comparisonVehicles.length,
      compareVehicleIds: comparisonVehicles.map((vehicle) => vehicle.vehicleId),
    })
  }

  useEffect(() => {
    if (comparisonVehicles.length === 0) {
      setCompareBarExpanded(true)
    }
  }, [comparisonVehicles])

  return (
    <section
      className={cx(styles.compareBar, {
        [styles.compareBarUnstuck]: !compareBarSticky,
      })}
      id="compare"
      aria-labelledby="compare-list-heading"
      aria-describedby={compareBarExpanded ? 'compare-list-body' : undefined}
      hidden={comparisonVehicles.length === 0 || customerHasActiveOrder}
    >
      <div className={styles.compareBarCollapsedView}>
        <Text
          className={styles.compareBarExpandedTitle}
          element={'a'}
          id={!compareBarExpanded ? 'compare-list-heading' : undefined}
          hidden={compareBarExpanded}
        >
          Your compare list{' '}
          {comparisonVehicles.length > 0 && `(${comparisonVehicles.length})`}
        </Text>

        <button
          type="button"
          className={styles.compareBarExpansionButton}
          onClick={() => handleCompareBarToggle(!compareBarExpanded)}
          aria-expanded={compareBarExpanded}
          aria-label={`${
            compareBarExpanded ? 'Collapse' : 'Expand'
          } compare bar`}
        >
          <SvgIcon name="chevron-down" size={16} color="mineShaft" />
        </button>
      </div>

      <div
        hidden={!compareBarExpanded}
        className={cx(styles.container, {
          [styles.containerExpanded]: compareBarExpanded,
        })}
      >
        <div className={styles.compareBarContent}>
          <Text
            className={styles.compareBarHeading}
            element="h2"
            fontSize="l"
            fontWeight="bold"
            id={compareBarExpanded ? 'compare-list-heading' : undefined}
          >
            Your compare list
          </Text>
          <p className={styles.compareBarText} id="compare-list-body">
            Select up to {maxNumberOfItems} cars to compare them side-by-side.
          </p>

          <Primary
            aria-describedby={
              compareButtonDisabled ? 'compare-validation' : undefined
            }
            aria-disabled={compareButtonDisabled}
            role={compareButtonDisabled ? 'link' : undefined}
            href={compareButtonDisabled ? undefined : compareCtaUrl}
            element="a"
            classNames={{ button: styles.compareBarButtonDesktop }}
            onClick={handleCompareCtaClick}
          >
            Compare cars
          </Primary>
        </div>

        <ol className={styles.compareBarList}>
          {Array.from({ length: maxNumberOfItems }, renderCompareBarItems)}
        </ol>

        <Primary
          aria-describedby={
            compareButtonDisabled ? 'compare-validation' : undefined
          }
          aria-disabled={compareButtonDisabled}
          role={compareButtonDisabled ? 'link' : undefined}
          href={compareButtonDisabled ? undefined : compareCtaUrl}
          element="a"
          classNames={{ button: styles.compareBarButtonMobile }}
          isFullWidth={true}
          onClick={handleCompareCtaClick}
        >
          Compare cars
        </Primary>
      </div>

      {compareButtonDisabled && (
        <ScreenReaderText id="compare-validation" aria-hidden="true">
          You need to add at least 2 cars to the compare list. You currently
          have {comparisonVehicles.length}
        </ScreenReaderText>
      )}

      <a className={styles.compareBarHiddenLink} href="#results">
        Go back to results
      </a>
    </section>
  )
}
